import React, { useEffect, useRef, useState } from 'react'
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Popup,
  useMap,
  Marker,
  AttributionControl,
} from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import './GlobalLeafletMap.css'
import './GeoPopup.css'
import { PopupContentPark } from './PopupContentPark'
import { GeoJSON } from 'react-leaflet'
import moment from 'moment-timezone'
import { loadStateFromLocalStorage } from '../../redux/utilsLocalStorage'
import { getEnvValue } from '../../env'

const convertUtcStringToTimezone = (utcString, timezone) => {
  return moment
    .tz(utcString, 'YYYY-MM-DD HH:mm:ss', 'UTC') // <-- updated input format
    .tz(timezone)
    .format('YYYY-MM-DD HH:mm')
}

const createPopupHtml = (p, latestUpdate, unit) => {
  // if the value is null then swho empty string
  const v = x => x ?? ''
  const pwr = x => {
    if (x == null) return ''
    const isMega = unit === 'MW' || unit === 'MWh'
    return Number((isMega ? x / 1000 : x).toFixed(3))
  }
  const ts = latestUpdate ?? '—'
  return `
    <div class="geo-popup-root">
      <div class="geo-popup-title">${p.name}</div>

      <table class="geo-popup-table">
        <tr>
          <th class="geo-popup-th"></th>
          <th class="geo-popup-th">now</th>
          <th class="geo-popup-th">-10</th>
          <th class="geo-popup-th">-20</th>
          <th class="geo-popup-th">-30</th>
        </tr>

        <tr>
          <td class="geo-popup-td">Power (${unit})</td>
          <td class="geo-popup-td">${pwr(p.power.now)}</td>
          <td class="geo-popup-td">${pwr(p.power['-10'])}</td>
          <td class="geo-popup-td">${pwr(p.power['-20'])}</td>
          <td class="geo-popup-td">${pwr(p.power['-30'])}</td>
        </tr>

        <tr>
          <td class="geo-popup-td">Wind speed (m/s)</td>
          <td class="geo-popup-td">${v(p.windspeed.now)}</td>
          <td class="geo-popup-td">${v(p.windspeed['-10'])}</td>
          <td class="geo-popup-td">${v(p.windspeed['-20'])}</td>
          <td class="geo-popup-td">${v(p.windspeed['-30'])}</td>
        </tr>
      </table>

      <div class="geo-popup-updated">
          Latest update: ${ts}
        </div>
    </div>
  `
}

const createTurbineIcon = (label, status) => {
  const normalized = Number(status)

  const colorClass =
    normalized === 1 ? 'turbine-green' : normalized === 0 ? 'turbine-red' : ''

  const text = String(label ?? '')
  const len = text.length

  const height = 20
  const baseWidth = 24
  const width = len <= 3 ? baseWidth : baseWidth + (len - 3) * 8

  const wideClass = len > 3 ? 'turbine-wide' : ''

  return L.divIcon({
    className: 'turbine-icon',
    html: `
      <div class="turbine ${colorClass} ${wideClass}">
        ${text}
      </div>
    `,
    iconSize: [width, height],
    iconAnchor: [width / 2, height / 2],
  })
}

const CARTO_API_KEY = getEnvValue('REACT_APP_CARTO_API_KEY')
const TILE_GRAY = `https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=${encodeURIComponent(
  CARTO_API_KEY || '',
)}`
const TILE_TERRAIN =
  'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
// Other good ones: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png'

const CustomLayerSelector = ({ baseLayer, setBaseLayer }) => {
  return (
    <div
      style={{
        position: 'absolute',
        top: 10,
        right: 10,
        background: 'white',
        zIndex: 1000,
        borderRadius: 8,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        padding: '12px',
      }}
    >
      <div>
        <label>
          <input
            type="radio"
            checked={baseLayer === 'baseMap'}
            style={{ cursor: 'pointer' }}
            onChange={() => setBaseLayer('baseMap')}
          />
          Base Map
        </label>
      </div>
      <div>
        <label>
          <input
            type="radio"
            checked={baseLayer === 'sat'}
            style={{ cursor: 'pointer' }}
            onChange={() => setBaseLayer('sat')}
          />
          Satellite/Terrain
        </label>
      </div>
    </div>
  )
}

/**
 * Waits for Leaflet's internal initialization before rendering children,
 * and cleans up animations on unmount to prevent _leaflet_pos errors.
 */
const MapReady = ({ children }) => {
  const map = useMap()
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (map.getSize().x > 0 && map.getSize().y > 0) {
      // Map already has a valid container size — it's ready
      setReady(true)
    } else {
      // Wait for Leaflet to signal readiness
      const onReady = () => setReady(true)
      map.whenReady(onReady)
    }

    return () => {
      // Cleanup: stop animations and detach listeners before DOM is destroyed
      try {
        map.stop()

        // Kill CSS transitions on the map pane so transitionend
        // callbacks cannot fire after the DOM element is removed
        const pane = map.getPane('mapPane')
        if (pane) {
          pane.style.transition = 'none'
          pane.style.transform = ''
        }

        map.off()
      } catch (e) {
        // Ignore errors during cleanup
      }
    }
  }, [map])

  // Also invalidate size once ready so Leaflet measures tiles correctly
  useEffect(() => {
    if (ready) {
      const timer = setTimeout(() => map.invalidateSize(), 0)
      return () => clearTimeout(timer)
    }
  }, [ready, map])

  return ready ? children : null
}

const ResetViewControl = ({ boundsRef }) => {
  const map = useMap()
  useEffect(() => {
    const controlDiv = L.DomUtil.create(
      'div',
      'leaflet-bar leaflet-control leaflet-control-custom',
    )
    controlDiv.style.backgroundColor = 'white'
    controlDiv.style.width = '30px'
    controlDiv.style.height = '30px'
    controlDiv.style.display = 'flex'
    controlDiv.style.alignItems = 'center'
    controlDiv.style.justifyContent = 'center'
    controlDiv.style.cursor = 'pointer'
    controlDiv.title = 'Reset Zoom'
    controlDiv.innerHTML = `
      <svg fill="#333" width="20" height="20" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
        <path d="M64,256H34A222,222,0,0,1,430,118.15V85h30V190H355V160h67.27A192.21,192.21,0,0,0,256,64C150.13,64,64,150.13,64,256Zm384,0c0,105.87-86.13,192-192,192A192.21,192.21,0,0,1,89.73,352H157V322H52V427H82V393.85A222,222,0,0,0,478,256Z"/>
      </svg>`

    controlDiv.onclick = () => {
      if (boundsRef.current) {
        map.fitBounds(boundsRef.current, { padding: [40, 40] })
      }
    }

    const customControl = L.control({ position: 'topleft' })
    customControl.onAdd = () => controlDiv
    customControl.addTo(map)

    return () => {
      customControl.remove()
    }
  }, [map, boundsRef])

  return null
}

const SmartAutoZoom = ({ data, turbineData, showGeoLayer, boundsRef }) => {
  const map = useMap()

  const plantPositions = data.map(d => [d.latitude, d.longitude])
  const hasPolygon = Boolean(turbineData?.length)

  useEffect(() => {
    if (!plantPositions.length) return

    // Small delay to ensure map is fully ready
    const timer = setTimeout(() => {
      // Calculate plant bounds
      const plantBounds = L.latLngBounds(plantPositions)

      // If we have polygons, calculate which bounds are larger or combine them
      if (hasPolygon) {
        try {
          const layer = L.geoJSON(turbineData)
          const polygonBounds = layer.getBounds()

          // Extend plant bounds to include polygon bounds (union of both)
          const combinedBounds = plantBounds.extend(polygonBounds)

          // Store combined bounds for reset button
          boundsRef.current = combinedBounds

          // Use combined bounds for fitting
          map.fitBounds(combinedBounds, { padding: [50, 50] })
          return
        } catch (e) {
          console.warn('Polygon bounds calculation failed:', e)
        }
      }

      // No turbineData - use plant bounds
      if (plantPositions.length > 1) {
        boundsRef.current = plantBounds
        map.fitBounds(plantBounds, { padding: [40, 40] })
        return
      }

      // Single plant → center and zoom
      boundsRef.current = plantBounds
      map.setView(plantPositions[0], 16)
    }, 100)

    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    plantPositions.length,
    showGeoLayer,
    hasPolygon,
    turbineData,
    map,
    boundsRef,
  ])

  return null
}

const GeoLayerToggle = ({ visible, checked, onChange }) => {
  if (!visible) return null

  return (
    <div
      style={{
        position: 'absolute',
        top: 85, // BELOW CustomLayerSelector
        right: 10,
        zIndex: 1000,
        background: 'white',
        padding: '6px 11px',
        borderRadius: 8,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        cursor: 'pointer',
      }}
    >
      <label>
        <input type="checkbox" checked={checked} onChange={onChange} />
        &nbsp; Show Availability Map
      </label>
    </div>
  )
}

const GlobalLeafletMap = props => {
  const {
    data = [],
    turbineData = [],
    latestUpdate,
    unit,
    isOverviewDAH,
    mapKey,
    showGeoLayer: showGeoLayerProp,
    onToggleGeoLayer,
  } = props

  const [baseLayer, setBaseLayer] = useState('baseMap')

  // Use prop if provided, otherwise fall back to local state
  const showGeoLayer = showGeoLayerProp !== undefined ? showGeoLayerProp : true
  const handleToggle = onToggleGeoLayer || (() => {})

  const positions = data.map(d => [d.latitude, d.longitude])
  const center = positions.length
    ? [
        (Math.max(...positions.map(p => p[0])) +
          Math.min(...positions.map(p => p[0]))) /
          2,
        (Math.max(...positions.map(p => p[1])) +
          Math.min(...positions.map(p => p[1]))) /
          2,
      ]
    : [44.7866, 20.4489]

  const initialBoundsRef = useRef(null)

  // use this timezone from store because of refreshing page
  const timezoneStore =
    loadStateFromLocalStorage('timezone') !== undefined
      ? loadStateFromLocalStorage('timezone')
      : 'Etc/UTC'

  /* 
  This is the part for the new layer regarding turbine level to be visible
  */
  const showGeoToggle = turbineData.length > 0 // auto-computed, no need for state
  const polygons = turbineData
  const turbines = turbineData.flatMap(f => f.properties?.turbines || [])

  // If turbineData exists, use a fixed radius for all circles
  const hasTurbineDataLocal = turbineData.length > 0
  const FIXED_RADIUS = 10

  return (
    <div style={{ height: '100%', width: '100%' }}>
      <MapContainer
        key={mapKey}
        center={center}
        zoom={4}
        minZoom={4}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom
        attributionControl={false}
      >
        <TileLayer
          url={baseLayer === 'baseMap' ? TILE_GRAY : TILE_TERRAIN}
          attribution={
            baseLayer === 'baseMap'
              ? '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
              : 'Tiles &copy; Esri'
          }
        />
        <AttributionControl prefix={false} />

        <MapReady>
          <ResetViewControl boundsRef={initialBoundsRef} />

          <CustomLayerSelector
            baseLayer={baseLayer}
            setBaseLayer={setBaseLayer}
          />

          <GeoLayerToggle
            visible={showGeoToggle}
            checked={showGeoLayer}
            onChange={() => handleToggle(!showGeoLayer)}
          />

          {/** Control all curent zoom levels */}
          <SmartAutoZoom
            data={data}
            turbineData={turbineData}
            showGeoLayer={showGeoLayer}
            boundsRef={initialBoundsRef}
          />

          {/* TURBINE MARKERS */}
          {showGeoLayer &&
            turbines.map((t, idx) => {
              const icon = createTurbineIcon(t.name, t.status)
              return (
                <Marker key={idx} position={[t.lat, t.lon]} icon={icon}>
                  <Popup className="turbine-popup">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: createPopupHtml(
                          t,
                          convertUtcStringToTimezone(
                            latestUpdate,
                            timezoneStore,
                          ),
                          unit,
                        ),
                      }}
                    />
                  </Popup>
                </Marker>
              )
            })}

          {/* TURBINE POLYGONS */}
          {showGeoLayer &&
            polygons.map(feat => (
              <GeoJSON
                key={`${feat.properties.name}_${latestUpdate || ''}`}
                data={feat}
                style={() => ({
                  color: '#3388ff',
                  weight: 2,
                  fillOpacity: 0.15,
                })}
                onEachFeature={(feature, layer) => {
                  const p = feature.properties
                  const html = createPopupHtml(
                    p,
                    convertUtcStringToTimezone(latestUpdate, timezoneStore),
                    unit,
                  )
                  layer.bindPopup(html, { className: 'area-popup' })
                }}
              />
            ))}

          {/* Circles */}
          {data.map((d, idx) => {
            // Use fixed radius if turbine data exists, otherwise use calculated radius
            const circleRadius = hasTurbineDataLocal ? FIXED_RADIUS : d.radius

            return (
              <React.Fragment key={idx}>
                {/* Proportional visual */}
                <CircleMarker
                  center={[d.latitude, d.longitude]}
                  radius={circleRadius}
                  pathOptions={{
                    fillColor: d.color === 'transparent' ? '#fff' : d.color,
                    color: '#444',
                    weight: 1,
                    fillOpacity: 0.7,
                    opacity: 0.85,
                  }}
                  interactive={false}
                />

                {/* Precise marker (for popup) */}
                <Marker
                  position={[d.latitude, d.longitude]}
                  icon={d.invisibleIcon}
                >
                  <Popup className={isOverviewDAH ? '' : d.popupClass}>
                    <PopupContentPark
                      data={d}
                      unit={unit}
                      color={d.color}
                      isOverviewDAH={isOverviewDAH}
                    />
                  </Popup>
                </Marker>
              </React.Fragment>
            )
          })}
        </MapReady>
      </MapContainer>
    </div>
  )
}

export default GlobalLeafletMap
